import { FC, useEffect, useState } from 'react';

import { Stack } from '@mui/material';
import { NumberInputBasic } from './NumberInput';
import { FormattedMessage, useIntl } from 'react-intl';
import { DocumentType, graphql } from '../../../../../gql';
import { useApiMutation, useApiQuery } from '../../../../../query/hooks';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { useDebounceEffect } from '../../../../../utils/useDebounceEffect';
import { useSnackbar } from '../../../../../store/snackbar';

const getContentMetricBounds = graphql(`
	query metrics_getMetricBounds($contentMetricId: UUID!) {
		getContentMetric(by: { id: $contentMetricId }) {
			metric {
				lowerWarningBound
				upperWarningBound
			}
		}
	}
`);

const updateMetricBoundMutation = graphql(`
	mutation metrics_updateMetricBound($contentMetricId: UUID!, $data: ContentMetricUpdateInput!) {
		updateContentMetric(by: { id: $contentMetricId }, data: $data) {
			ok
			errorMessage
		}
	}
`);

type WariningBounds = NonNullable<DocumentType<typeof getContentMetricBounds>>['getContentMetric'];
type Props = Readonly<{
	contentMetricId: string;
}>;

const AlertsDrawerTab: FC<Props> = ({ contentMetricId }) => {
	const intl = useIntl();
	const { data, isPending, isLoading } = useApiQuery(getContentMetricBounds, { variables: { contentMetricId } });
	const { mutate: updateWarningBound } = useApiMutation(updateMetricBoundMutation);
	const { openSnackbar } = useSnackbar();

	const warningBoundsData: WariningBounds = data?.getContentMetric ?? {};

	const [warningBounds, setWarningBounds] = useState<[number | null, number | null]>([
		warningBoundsData.metric?.lowerWarningBound ?? null,
		warningBoundsData.metric?.upperWarningBound ?? null,
	]);

	const [initialLoad, setInitialLoad] = useState(false);
	const [isErrors, setIsErrors] = useState(false);

	const validateBounds = (lowerBound: number | null, upperBound: number | null) => {
		if (lowerBound !== null && upperBound !== null) {
			if (lowerBound > upperBound || upperBound < lowerBound) {
				setIsErrors(true);
				return true;
			}
		}
		setIsErrors(false);
		return false;
	};

	const handleWarningBound = (lowerWarningBound: number | null, upperWarningBound: number | null) => {
		updateWarningBound(
			{ contentMetricId, data: { metric: { update: { lowerWarningBound: lowerWarningBound, upperWarningBound: upperWarningBound } } } },
			{
				onSuccess: (result) => {
					mutationResponseHandler({
						result: result.updateContentMetric.ok,
						toastMessages: {
							success: intl.formatMessage({ id: 'metrics-widget.tab.alerts.alert-at-drop.success' }),
							error: intl.formatMessage({ id: 'metrics-widget.tab.alerts.alert-at-drop.error' }),
						},
						errorMessage: result.updateContentMetric.errorMessage,
						openSnackbar,
					});
				},
			},
		);
	};

	useEffect(() => {
		if (!isLoading) {
			setWarningBounds([
				typeof warningBoundsData.metric?.lowerWarningBound === 'number' ? warningBoundsData.metric.lowerWarningBound : null,
				typeof warningBoundsData.metric?.upperWarningBound === 'number' ? warningBoundsData.metric.upperWarningBound : null,
			]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading]);

	useDebounceEffect(
		() => {
			validateBounds(warningBounds[0], warningBounds[1]);
			if (!validateBounds(warningBounds[0], warningBounds[1]) && initialLoad && !isLoading) {
				!isPending && handleWarningBound(warningBounds[0], warningBounds[1]);
			}
			if (!initialLoad && !isLoading) {
				setInitialLoad(true);
			}
		},
		[warningBounds[0], warningBounds[1], isErrors],
		1500,
	);

	return (
		<Stack gap={2}>
			<NumberInputBasic
				label={<FormattedMessage id={'metrics-widget.tab.alerts.alert-at-drop'} />}
				error={isErrors && <FormattedMessage id={'metrics-widget.tab.alerts.form.alert-at-drop.error'} />}
				value={warningBounds[0]}
				setValue={(value) => setWarningBounds((prevState) => [value as number, prevState[1]])}
			/>
			<NumberInputBasic
				label={<FormattedMessage id={'metrics-widget.tab.alerts.alert-when-reaching'} />}
				error={isErrors && <FormattedMessage id={'metrics-widget.tab.alerts.form.alert-when-reaching.error'} />}
				value={warningBounds[1]}
				setValue={(value) => setWarningBounds((prevState) => [prevState[0], value as number])}
			/>
		</Stack>
	);
};

export { AlertsDrawerTab };
